import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, ChevronDown, Plus, Minus, Trash2, Briefcase, RefreshCw, Search, TrendingUp, TrendingDown, DollarSign, PieChart, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { getCachedNewsData, processNewsFlags } from '../utils/newsCache';
import { renderNewsRating } from '../utils/renderNewsRating';
import {
  getCachedPortfolioMap, setCachedPortfolioMap, updateCachedPortfolioMap, getCachedPortfolio,
  getCachedPortfolioData, setCachedPortfolioDataWithDate, clearCachedPortfolioData,
  invalidatePortfolioDataOnNewTradeDate, clearCachedPortfolioMap
} from '../utils/portfolioWatchlistCache';
import { getCachedTradeDates, getCachedLatestTradeDatesByMarket, getLatestTradeDateKeySync } from '../utils/tradeDatesCache';
import { buildPortfolioDisplayData, clearPwStockIndexCacheAndStocksMemory } from '../utils/portfolioWatchlistStockData';
import { serializeLatestByMarket } from '../utils/marketTradeDates';
import SymbolAutocomplete from './SymbolAutocomplete';
import { applyLiveQuotesToPortfolio } from '../utils/liveMarketData';
import { useLiveMarketDisplay } from '../hooks/useLiveMarketDisplay';
import { authFetch } from '../utils/authFetch';

function resolveSymbolIdFromMap(raw, symbolMap) {
  if (!raw || !symbolMap) return null;
  const t = raw.trim();
  if (symbolMap[t]) return t;
  return Object.keys(symbolMap).find((k) => k.toUpperCase() === t.toUpperCase()) || null;
}

const Portfolio = () => {
  const [portfolio, setPortfolio] = useState({});
  const [portfolioMap, setPortfolioMap] = useState({});
  const [portfolioNames, setPortfolioNames] = useState([]);
  const [activePortfolio, setActivePortfolio] = useState(null);
  const [rows, setRows] = useState([{ id: 1, symbol: '', price: '', qty: '1', actionType: 'long', date: null, resolvedId: null }]);
  const [pendingConfirm, setPendingConfirm] = useState(null); // { toRemove, toProcess }
  const [user, setUser] = useState(null);
  const [page, setPage] = useState(1);
  const [portfolioStocks, setPortfolioStocks] = useState([]);
  const [portfolioSummary, setPortfolioSummary] = useState({
    avg1WeekPerformance: 0,
    avg4WeekPerformance: 0,
    avgReturns: 0,
    avgPercentGain: 0,
    totalCost: 0,
    totalMarketValue: 0,
  });
  const [allTradeDates, setAllTradeDates] = useState([]);
  const [selectedKeys, setSelectedKeys] = useState(new Set());
  const [isUpdating, setIsUpdating] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newPortfolioName, setNewPortfolioName] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [portfolioToDelete, setPortfolioToDelete] = useState(null);
  const [newsFlags, setNewsFlags] = useState({}); // { symbol_id: 'grey' | 'red' | 'amber' | 'green' }
  const [newsFlagRatings, setNewsFlagRatings] = useState({}); // { symbol_id: number | null }
  const [isLoadingPortfolioMap, setIsLoadingPortfolioMap] = useState(false);
  const [isLoadingPortfolioData, setIsLoadingPortfolioData] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [tableSearch, setTableSearch] = useState('');
  const [sortField, setSortField] = useState('symbol');
  const [sortDirection, setSortDirection] = useState('asc');
  const [showManagePanel, setShowManagePanel] = useState(false);
  const [autoOpenedByCheckbox, setAutoOpenedByCheckbox] = useState(false);

  const itemsPerPage = 10;

  const PORTFOLIO_COLUMNS = [
    { key: 'symbol', label: 'Symbol', type: 'string' },
    { key: 'name', label: 'Company', type: 'string' },
    { key: 'sector', label: 'Sector', type: 'string' },
    { key: 'action_type', label: 'Action', type: 'string' },
    { key: 'close', label: 'Price', type: 'number' },
    { key: 'days_change', label: '% Change', type: 'number' },
    { key: 'trade_timestamp', label: 'Updated At', type: 'date' },
    { key: 'price_ext', label: 'Price Ext.', type: 'number' },
    { key: 'pct_change_ext', label: '% Change Ext', type: 'number' },
    { key: 'buy_price', label: 'Buy Price', type: 'number' },
    { key: 'quantity', label: 'Qty', type: 'number' },
    { key: 'cost', label: 'Cost', type: 'number' },
    { key: 'market_value', label: 'Mkt Value', type: 'number' },
    { key: 'performance', label: 'Returns', type: 'number' },
    { key: 'buy_date', label: 'Buy Date', type: 'date' },
    { key: 'status', label: 'Signal', type: 'string' },
  ];

  const colByKey = (key) => PORTFOLIO_COLUMNS.find((c) => c.key === key);

  const handleColumnSort = (fieldKey) => {
    setPage(1);
    if (sortField === fieldKey) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : prev === 'desc' ? 'neutral' : 'asc'));
    } else {
      setSortField(fieldKey);
      setSortDirection('asc');
    }
  };

  const compareSortValues = (a, b, type, order) => {
    const aEmpty = a == null || a === '' || a === '--';
    const bEmpty = b == null || b === '' || b === '--';
    if (aEmpty && bEmpty) return 0;
    if (aEmpty) return 1;
    if (bEmpty) return -1;
    let cmp = 0;
    if (type === 'number') {
      cmp = Number(a) - Number(b);
      if (!Number.isFinite(cmp)) cmp = 0;
    } else if (type === 'date') {
      cmp = String(a).localeCompare(String(b));
    } else {
      cmp = String(a).localeCompare(String(b), undefined, { sensitivity: 'base' });
    }
    return order === 'desc' ? -cmp : cmp;
  };

  const fetchPortfolioMap = useCallback(async (userEmail, forceRefresh = false) => {
    try {
      if (!forceRefresh) {
        const cachedMap = getCachedPortfolioMap(userEmail);
        if (cachedMap && Object.keys(cachedMap).length > 0) {
          setPortfolioMap(cachedMap);
          const names = Object.keys(cachedMap);
          setPortfolioNames(names);

          if (!activePortfolio || !names.includes(activePortfolio)) {
            if (names.length > 0) {
              setActivePortfolio(names[0]);
            } else {
              setActivePortfolio(null);
            }
          }

          return cachedMap;
        }
      }

      const response = await authFetch(`/api/portfolio/${userEmail}`);
      if (!response.ok) {
        console.error('Error fetching portfolio map:', response.status, await response.text());
        return {};
      }
      const data = await response.json();
      const map = data.portfolioMap || {};

      if (Object.keys(map).length > 0) {
        setCachedPortfolioMap(userEmail, map);
      } else {
        clearCachedPortfolioMap(userEmail);
      }

      setPortfolioMap(map);
      const names = Object.keys(map);
      setPortfolioNames(names);

      if (!activePortfolio || !names.includes(activePortfolio)) {
        if (names.length > 0) {
          setActivePortfolio(names[0]);
        } else {
          setActivePortfolio(null);
        }
      }
      return map;
    } catch (error) {
      console.error('Error fetching portfolio map:', error);
      return {};
    }
  }, [activePortfolio]);

  const fetchAndCacheData = useCallback(async (userEmail, portfolioName, forceRefresh = false) => {
    if (!portfolioName) return;

    const defaultSummary = {
      avg1WeekPerformance: 0,
      avg4WeekPerformance: 0,
      avgReturns: 0,
      avgPercentGain: 0,
      totalCost: 0,
      totalMarketValue: 0,
    };

    try {
      if (!forceRefresh) {
        // Trade-date-aware cache read: getCachedPortfolioData automatically
        // invalidates entries whose tradeDate doesn't match the latest.
        const cached = getCachedPortfolioData(userEmail, portfolioName);
        if (cached) {
          console.log(`[PORTFOLIO DATA] Using cached portfolio data for ${portfolioName}`);
          setPortfolio(cached.portfolio || {});
          setPortfolioStocks(cached.portfolioStocks || []);
          setPortfolioSummary(cached.portfolioSummary || defaultSummary);
          return;
        }
      }

      // No valid portfolioData cache, check if portfolio symbols exist in map cache
      const cachedPortfolio = getCachedPortfolio(userEmail, portfolioName);
      console.log(`[PORTFOLIO DATA] Cached portfolio symbols for ${userEmail}/${portfolioName}:`, cachedPortfolio);

      if (cachedPortfolio !== null) {
        const portfolioSymbols = cachedPortfolio;

        if (Object.keys(portfolioSymbols).length === 0) {
          const latestByMarket = await getCachedLatestTradeDatesByMarket();
          const tradeDateKey = getLatestTradeDateKeySync() || serializeLatestByMarket(latestByMarket);
          const emptyData = { portfolio: {}, portfolioStocks: [], portfolioSummary: defaultSummary };
          setCachedPortfolioDataWithDate(userEmail, portfolioName, emptyData, tradeDateKey);
          setPortfolio(emptyData.portfolio);
          setPortfolioStocks(emptyData.portfolioStocks);
          setPortfolioSummary(emptyData.portfolioSummary);
          return;
        }

        await fetchFreshPortfolioData(userEmail, portfolioName, portfolioSymbols, forceRefresh);
        return;
      }

      // Portfolio not in cache, fetch from API
      const portfolioResponse = await authFetch(`/api/portfolio/${userEmail}?name=${portfolioName}`);
      const portfolioData = await portfolioResponse.json();
      const portfolioSymbols = portfolioData.portfolio || {};

      if (Object.keys(portfolioSymbols).length === 0) {
        const latestByMarket = await getCachedLatestTradeDatesByMarket();
        const tradeDateKey = getLatestTradeDateKeySync() || serializeLatestByMarket(latestByMarket);
        const emptyData = { portfolio: {}, portfolioStocks: [], portfolioSummary: defaultSummary };
        setCachedPortfolioDataWithDate(userEmail, portfolioName, emptyData, tradeDateKey);
        setPortfolio(emptyData.portfolio);
        setPortfolioStocks(emptyData.portfolioStocks);
        setPortfolioSummary(emptyData.portfolioSummary);
        return;
      }

      await fetchFreshPortfolioData(userEmail, portfolioName, portfolioSymbols, forceRefresh);
    } catch (error) {
      console.error('Error fetching portfolio data:', error);
    }
  }, []);

  const fetchFreshPortfolioData = async (userEmail, portfolioName, portfolio, forceRefresh = false) => {
    try {
      const display = await buildPortfolioDisplayData(portfolio, { forceRefresh });
      if (!display) {
        console.warn('[PORTFOLIO DATA] Could not build US/CA stock display data');
        return;
      }

      const { portfolioStocks, portfolioSummary, tradeDateKey } = display;
      const portfolioKeys = Object.keys(portfolio);

      setPortfolio(portfolio);
      setPortfolioStocks(portfolioStocks);
      setPortfolioSummary(portfolioSummary);

      if (portfolioStocks.length === 0 && portfolioKeys.length > 0) {
        console.log('[PORTFOLIO DATA CACHE] Portfolio has symbols but no matching stocks, skipping cache write');
        return;
      }

      const newCacheData = {
        portfolio,
        portfolioStocks,
        portfolioSummary,
      };

      setCachedPortfolioDataWithDate(
        userEmail,
        portfolioName,
        newCacheData,
        tradeDateKey || getLatestTradeDateKeySync()
      );
    } catch (error) {
      console.error('Error fetching fresh portfolio data:', error);
    }
  };

  const loadCachedData = useCallback((userEmail, portfolioName) => {
    if (!userEmail || !portfolioName) return false;

    const cached = getCachedPortfolioData(userEmail, portfolioName);
    if (cached) {
      setPortfolio(cached.portfolio || {});
      setPortfolioStocks(cached.portfolioStocks || []);
      setPortfolioSummary(cached.portfolioSummary || {
        avg1WeekPerformance: 0,
        avg4WeekPerformance: 0,
        avgReturns: 0,
        avgPercentGain: 0,
        totalCost: 0,
        totalMarketValue: 0,
      });
      return true;
    }
    return false;
  }, []);

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem('user'));
    setUser(storedUser);
    if (storedUser) {
      setIsLoadingPortfolioMap(true);

      // Drop stale empty map caches left from pre-auth API failures.
      const cachedMap = getCachedPortfolioMap(storedUser.email);
      if (cachedMap && Object.keys(cachedMap).length === 0) {
        clearCachedPortfolioMap(storedUser.email);
      }

      // Eagerly invalidate stale portfolio data caches before any reads.
      const tradeDateKey = getLatestTradeDateKeySync();
      if (tradeDateKey) {
        invalidatePortfolioDataOnNewTradeDate(storedUser.email, tradeDateKey);
      } else {
        getCachedLatestTradeDatesByMarket().then((latestByMarket) => {
          const key = serializeLatestByMarket(latestByMarket);
          if (key && key !== 'US:|CA:') {
            invalidatePortfolioDataOnNewTradeDate(storedUser.email, key);
          }
        }).catch(() => { /* non-critical */ });
      }

      fetchPortfolioMap(storedUser.email)
        .then(() => {
          if (activePortfolio) {
            if (!loadCachedData(storedUser.email, activePortfolio)) {
              fetchAndCacheData(storedUser.email, activePortfolio);
            }
          }
        })
        .finally(() => setIsLoadingPortfolioMap(false));
      fetchAllTradeDates();
    }
  }, []);

  useEffect(() => {
    if (user && !isUpdating && activePortfolio) {
      // Only show spinner when there's no existing data to display (initial load);
      // otherwise keep current data visible while refreshing in background
      const hasExistingData = portfolioStocks && portfolioStocks.length > 0;
      if (!hasExistingData) {
        setIsLoadingPortfolioData(true);
      }
      fetchAndCacheData(user.email, activePortfolio)
        .finally(() => setIsLoadingPortfolioData(false));
    } else {
      setIsLoadingPortfolioData(false);
    }
  }, [user, isUpdating, activePortfolio, fetchAndCacheData]);

  // Fetch news flags for portfolio stocks
  useEffect(() => {
    if (portfolioStocks && portfolioStocks.length > 0) {
      fetchNewsFlags(portfolioStocks.map(stock => stock.symbol_id));
    } else {
      setNewsFlags({});
      setNewsFlagRatings({});
    }
  }, [portfolioStocks]);

  const portfolioSymbolIds = useMemo(
    () => (portfolioStocks || []).map((s) => s.symbol_id).filter(Boolean),
    [portfolioStocks]
  );

  useLiveMarketDisplay({
    enabled: Boolean(user) && portfolioSymbolIds.length > 0,
    symbolIds: portfolioSymbolIds,
    onApply: (live) => {
      setPortfolioStocks((prev) => {
        const { stocks, summary } = applyLiveQuotesToPortfolio(prev, live.quotes, live.extendedQuotes);
        if (summary) setPortfolioSummary(summary);
        return stocks;
      });
    }
  });

  const fetchNewsFlags = async (symbolIds) => {
    if (!symbolIds || symbolIds.length === 0) return;
    
    try {
      // Get current date in YYYY-MM-DD format
      const currentDate = new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York'});
      
      // Use shared utility to get cached news data
      const newsData = await getCachedNewsData(currentDate, true);
      
      // Process news data to get flags and ratings
      const { flags, ratings } = processNewsFlags(newsData, symbolIds);
      
      setNewsFlags(flags);
      setNewsFlagRatings(ratings);
    } catch (error) {
      console.error('Error fetching news flags:', error);
      // On error, set all flags to grey
      const flags = {};
      const ratings = {};
      symbolIds.forEach((id) => {
        flags[id] = 'grey';
        ratings[id] = null;
      });
      setNewsFlags(flags);
      setNewsFlagRatings(ratings);
    }
  };

  const fetchAllTradeDates = async () => {
    try {
      const dates = await getCachedTradeDates();
      const parsedDates = dates.map(date => new Date(date + 'T00:00:00'));
      setAllTradeDates(parsedDates);
    } catch (error) {
      console.error('Error fetching trade dates:', error);
    }
  };

  const newEmptyRow = () => ({ id: Date.now() + Math.random(), symbol: '', price: '', qty: '1', actionType: 'long', date: null, resolvedId: null });

  const addRow = () => setRows(prev => [...prev, newEmptyRow()]);

  const removeRow = (id) => setRows(prev => prev.length > 1 ? prev.filter(r => r.id !== id) : prev);

  const updateRow = (id, field, value) => {
    const { getCachedSymbolNamesSync } = require('../utils/symbolNamesCache');
    setRows(prev => prev.map(r => {
      if (r.id !== id) return r;
      const updated = { ...r, [field]: value };
      if (field === 'symbol') {
        const symbolMap = getCachedSymbolNamesSync();
        updated.resolvedId = resolveSymbolIdFromMap(value, symbolMap || {});
      }
      return updated;
    }));
  };

  // Applies all adds/updates/qty-only-updates/removes in sequence and refreshes state
  const processPositions = async (toAdd, toUpdate, toQtyUpdate, removeIds) => {
    const currentDate = new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
    let finalPortfolio = portfolio;
    let finalMap = portfolioMap;

    if (removeIds.length > 0) {
      const resp = await authFetch(`/api/portfolio/${user.email}/remove-multiple`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: activePortfolio, symbols: removeIds }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.message);
      finalPortfolio = data.portfolio;
      finalMap = data.portfolioMap;
    }

    if (toAdd.length > 0) {
      const payload = { name: activePortfolio };
      toAdd.forEach(r => {
        payload[r.resolvedId] = [parseFloat(r.price), r.formattedDate, currentDate, parseInt(r.qty), r.actionType];
      });
      const resp = await authFetch(`/api/portfolio/${user.email}/add-multiple`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.message);
      finalPortfolio = data.portfolio;
      finalMap = data.portfolioMap;
    }

    // Full update: new price + date provided → recalculates weighted avg price
    for (const r of toUpdate) {
      const resp = await authFetch(`/api/portfolio/${user.email}/update-position`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: activePortfolio,
          symbol_id: r.resolvedId,
          additional_quantity: parseInt(r.qty),
          new_price: parseFloat(r.price),
          buy_date: r.formattedDate,
          action_type: r.actionType,
        }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.message);
      finalPortfolio = data.portfolio;
      finalMap = data.portfolioMap;
    }

    // Qty-only update: no price/date → avg price and buy date unchanged
    for (const r of toQtyUpdate) {
      const resp = await authFetch(`/api/portfolio/${user.email}/update-quantity`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: activePortfolio,
          symbol_id: r.resolvedId,
          action_type: r.actionType,
          additional_quantity: parseInt(r.qty),
        }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.message);
      finalPortfolio = data.portfolio;
      finalMap = data.portfolioMap;
    }

    setPortfolio(finalPortfolio);
    setPortfolioMap(finalMap);
    setPortfolioNames(Object.keys(finalMap));
    if (user && finalMap) updateCachedPortfolioMap(user.email, finalMap);
    if (user && activePortfolio) clearCachedPortfolioData(user.email, activePortfolio);
    setRows([newEmptyRow()]);
    setPendingConfirm(null);
  };

  const removePosition = async (compositeKey) => {
    if (!user || !activePortfolio || isUpdating) return;
    setIsUpdating(true);
    try {
      const resp = await authFetch(`/api/portfolio/${user.email}/remove-multiple`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: activePortfolio, symbols: [compositeKey] }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.message);
      setPortfolio(data.portfolio);
      const updatedMap = data.portfolioMap;
      setPortfolioMap(updatedMap);
      setPortfolioNames(Object.keys(updatedMap));
      if (updatedMap) updateCachedPortfolioMap(user.email, updatedMap);
      clearCachedPortfolioData(user.email, activePortfolio);
    } catch (err) {
      alert(`Error removing position: ${err.message}`);
    } finally {
      setIsUpdating(false);
    }
  };

  const submitPositions = async (e) => {
    e.preventDefault();
    if (isUpdating) return;

    const { getCachedSymbolNamesSync } = require('../utils/symbolNamesCache');
    const symbolMap = getCachedSymbolNamesSync();

    // Resolve each row; relax the filter so existing positions don't require price/date
    const resolved = rows
      .map(r => ({
        ...r,
        resolvedId: r.resolvedId || resolveSymbolIdFromMap(r.symbol, symbolMap || {}),
        formattedDate: r.date ? r.date.toISOString().split('T')[0] : null,
      }))
      .filter(r => {
        if (!r.resolvedId || r.qty === '' || parseInt(r.qty) === 0) return false;
        const compositeKey = `${r.resolvedId}|${r.actionType}`;
        const isExisting = Boolean(portfolio[compositeKey]);
        // Existing position: qty-only update allowed (no price/date needed)
        if (isExisting) return true;
        // New position: price + date required
        return Boolean(r.price && r.formattedDate);
      });

    if (resolved.length === 0) return;

    const toAdd = [], toUpdate = [], toQtyUpdate = [], willGoNegative = [];

    for (const r of resolved) {
      const compositeKey = `${r.resolvedId}|${r.actionType}`;
      const existingEntry = portfolio[compositeKey];
      if (existingEntry) {
        const existingQty = parseInt(existingEntry[3]);
        const deltaQty = parseInt(r.qty);
        const finalQty = existingQty + deltaQty;
        if (finalQty < 1) {
          willGoNegative.push({ ...r, compositeKey, existingQty, finalQty });
        } else if (r.price && r.formattedDate) {
          // Full update: recalculate avg price with new price/date
          toUpdate.push({ ...r, compositeKey });
        } else {
          // Qty-only update: no price change
          toQtyUpdate.push({ ...r, compositeKey });
        }
      } else {
        if (parseInt(r.qty) < 1) continue;
        toAdd.push(r);
      }
    }

    if (willGoNegative.length > 0) {
      setPendingConfirm({ toRemove: willGoNegative, toProcess: { toAdd, toUpdate, toQtyUpdate } });
      return;
    }

    setIsUpdating(true);
    try {
      await processPositions(toAdd, toUpdate, toQtyUpdate, []);
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleConfirmRemove = async () => {
    if (!pendingConfirm) return;
    const { toRemove, toProcess } = pendingConfirm;
    setIsUpdating(true);
    try {
      await processPositions(toProcess.toAdd, toProcess.toUpdate, toProcess.toQtyUpdate || [], toRemove.map(r => r.resolvedId));
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setIsUpdating(false);
    }
  };

  const removeSelected = async () => {
    if (selectedKeys.size === 0 || !user || !activePortfolio || isUpdating) return;
    const symbols = Array.from(selectedKeys);

    setIsUpdating(true);
    try {
      const response = await authFetch(`/api/portfolio/${user.email}/remove-multiple`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: activePortfolio, symbols }),
      });
      const data = await response.json();
      if (response.ok) {
        setSelectedKeys(new Set());
        setPortfolio(data.portfolio);
        const updatedMap = data.portfolioMap || portfolioMap;
        setPortfolioMap(updatedMap);
        setPortfolioNames(Object.keys(updatedMap));
        if (user && updatedMap) updateCachedPortfolioMap(user.email, updatedMap);
        if (user && activePortfolio) clearCachedPortfolioData(user.email, activePortfolio);
      } else {
        alert(`Failed to remove positions: ${data.message}`);
      }
    } catch (error) {
      alert(`Error removing positions: ${error.message}`);
    } finally {
      setIsUpdating(false);
    }
  };


  const filteredStocks = useMemo(() => {
    const q = (tableSearch || '').trim().toLowerCase();
    if (!q) return portfolioStocks || [];
    return (portfolioStocks || []).filter((s) =>
      (s.symbol || '').toLowerCase().includes(q) ||
      (s.name || '').toLowerCase().includes(q) ||
      (s.sector || '').toLowerCase().includes(q)
    );
  }, [portfolioStocks, tableSearch]);

  const sortedStocks = useMemo(() => {
    const list = filteredStocks || [];
    if (sortDirection === 'neutral') return list;
    const col = PORTFOLIO_COLUMNS.find((c) => c.key === sortField);
    const type = col?.type || 'string';
    return [...list].sort((a, b) => {
      const primary = compareSortValues(a[sortField], b[sortField], type, sortDirection);
      if (primary !== 0) return primary;
      return compareSortValues(a.symbol, b.symbol, 'string', 'asc');
    });
  }, [filteredStocks, sortField, sortDirection]);

  const totalPages = Math.ceil((sortedStocks?.length || 0) / itemsPerPage);

  useEffect(() => {
    setPage(1);
  }, [sortedStocks?.length, tableSearch]);

  useEffect(() => {
    if (page > totalPages && totalPages > 0) {
      setPage(1);
    }
  }, [page, totalPages]);

  const paginatedStocks = sortedStocks?.slice((page - 1) * itemsPerPage, page * itemsPerPage) || [];
  const formatPercentage = (value) => {
    return value != null ? `${Number(value).toFixed(2)}%` : 'N/A';
  };

  const formatCurrency = (value) => {
    if (value == null || isNaN(value)) return '0.00';
    if (value >= 1_000_000_000) return `${(value / 1_000_000_000).toFixed(2)}B`;
    if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(2)}M`;
    if (value >= 1_000) return `${(value / 1_000).toFixed(2)}K`;
    return Number(value).toFixed(2);
  };

  const formatMarketCap = (marketCap) => {
    if (marketCap == null) return '--';
    if (marketCap >= 1000000000000) return `${(marketCap / 1000000000000).toFixed(2)}T`;
    if (marketCap >= 1000000000) return `${(marketCap / 1000000000).toFixed(2)}B`;
    if (marketCap >= 1000000) return `${(marketCap / 1000000).toFixed(2)}M`;
    if (marketCap >= 1000) return `${(marketCap / 1000).toFixed(2)}K`;
    return marketCap.toFixed(2);
  };

  const getNewsFlagTooltip = (flagColor) => {
    switch (flagColor) {
      case 'red':
        return 'Related news found with sentiment rating lte 4/10';
      case 'green':
        return 'Related news found with sentiment rating gte 7/10';
      case 'amber':
        return 'Related news found with sentiment rating eq 5/10|6/10';
      case 'grey':
      default:
        return 'No related news in cached latest news';
    }
  };

  useEffect(() => {
    if (selectedKeys.size > 0 && !showManagePanel) {
      setShowManagePanel(true);
      setAutoOpenedByCheckbox(true);
    } else if (selectedKeys.size === 0 && autoOpenedByCheckbox) {
      setShowManagePanel(false);
      setAutoOpenedByCheckbox(false);
    }
  }, [selectedKeys.size]);

  const portfolioCounts = useMemo(() => {
    const counts = {};
    portfolioNames.forEach((name) => {
      const map = portfolioMap?.[name];
      counts[name] = map ? Object.keys(map).length : 0;
    });
    return counts;
  }, [portfolioNames, portfolioMap]);

  const renderSortHeader = (col, extraClassName = '') => {
    const isActive = sortDirection !== 'neutral' && sortField === col.key;
    return (
      <th key={col.key} className={extraClassName} aria-sort={isActive ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}>
        <span className="screener-sort-header-inner">
          {col.label}
          <button
            type="button"
            className="screener-sort-trigger"
            onClick={() => handleColumnSort(col.key)}
            aria-label={`Sort by ${col.label}`}
          >
            <span className={`screener-sort-arrow screener-sort-arrow--up ${isActive && sortDirection === 'asc' ? 'screener-sort-icon--active' : 'screener-sort-icon--muted'}`} aria-hidden="true" />
            <span className={`screener-sort-arrow screener-sort-arrow--down ${isActive && sortDirection === 'desc' ? 'screener-sort-icon--active' : 'screener-sort-icon--muted'}`} aria-hidden="true" />
          </button>
        </span>
      </th>
    );
  };

  const totalPL = (portfolioSummary.totalMarketValue || 0) - (portfolioSummary.totalCost || 0);
  const totalPLPct = portfolioSummary.avgPercentGain || 0;
  const avgReturns = portfolioSummary.avgReturns != null
    ? portfolioSummary.avgReturns
    : (portfolioStocks?.length
        ? portfolioStocks.reduce((sum, s) => sum + (s.performance ?? 0), 0) / portfolioStocks.length
        : 0);

  const handlePortfolioTabChange = (portfolioName) => {
    setActivePortfolio(portfolioName);
    setPage(1);
    setSelectedKeys(new Set());
    if (user) {
      fetchAndCacheData(user.email, portfolioName);
    }
  };

  const handleCreatePortfolio = async () => {
    if (!user) return;
    
    // Generate default name if not provided
    let portfolioName = newPortfolioName.trim();
    if (!portfolioName) {
      const existingNames = portfolioNames.filter(n => n.startsWith('portfolio_'));
      const nextNum = existingNames.length > 0 
        ? Math.max(...existingNames.map(n => {
            const match = n.match(/portfolio_(\d+)/);
            return match ? parseInt(match[1]) : 0;
          })) + 1
        : 1;
      portfolioName = `portfolio_${nextNum}`;
    }

    try {
      const response = await authFetch(`/api/portfolio/${user.email}/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: portfolioName }),
      });
      const data = await response.json();
      if (response.ok) {
        setNewPortfolioName('');
        setShowCreateModal(false);
        const updatedMap = data.portfolioMap || {};
        
        // Update cache after successful create
        if (updatedMap) {
          updateCachedPortfolioMap(user.email, updatedMap);
        }
        
        await fetchPortfolioMap(user.email);
        setActivePortfolio(portfolioName);
      } else {
        alert(`Failed to create portfolio: ${data.message}`);
      }
    } catch (error) {
      console.error('Error creating portfolio:', error);
      alert(`Error creating portfolio: ${error.message}`);
    }
  };

  const handleDeletePortfolio = async () => {
    if (!portfolioToDelete || !user) return;

    try {
      const response = await authFetch(`/api/portfolio/${user.email}/${portfolioToDelete}`, {
        method: 'DELETE',
      });
      const data = await response.json();
      if (response.ok) {
        const updatedMap = data.portfolioMap || {};
        
        // Update cache after successful delete
        if (updatedMap) {
          updateCachedPortfolioMap(user.email, updatedMap);
        }
        
        await fetchPortfolioMap(user.email);
        setShowDeleteConfirm(false);
        setPortfolioToDelete(null);
        if (portfolioNames.length <= 1) {
          setActivePortfolio(null);
        }
      } else {
        alert(`Failed to delete portfolio: ${data.message}`);
      }
    } catch (error) {
      console.error('Error deleting portfolio:', error);
      alert(`Error deleting portfolio: ${error.message}`);
    }
  };

  const handleRefresh = async () => {
    if (!user || isRefreshing) return;
    setIsRefreshing(true);
    try {
      clearPwStockIndexCacheAndStocksMemory();
      portfolioNames.forEach(name => clearCachedPortfolioData(user.email, name));
      const freshMap = await fetchPortfolioMap(user.email, true);
      const allNames = Object.keys(freshMap || {});
      await Promise.all(allNames.map(name => fetchAndCacheData(user.email, name, true)));
    } catch (error) {
      console.error('Error refreshing portfolio:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const isPortfolioEmpty = portfolio && Object.keys(portfolio).length === 0;
  const canCreatePortfolio = portfolioNames.length <= 1; // Show create when 0 or 1 portfolio exists

  if (!user) {
    return (
        <div className="page-content portfolio-page pw-page">
          <div className="pw-login-prompt">
            <Briefcase size={48} />
            <h2>Manage Your Portfolio</h2>
            <p>Please <Link to="/login">log in</Link> to view and manage your portfolio.</p>
          </div>
        </div>
    );
  }

  if (isLoadingPortfolioMap) {
    return (
        <div className="page-content portfolio-page pw-page">
          <div className="pw-header">
            <div className="pw-header-left">
              <div className="pw-header-text">
                <h2 className="pw-title">
                  <Briefcase size={24} className="pw-title-icon pw-title-icon--portfolio" />
                  <span>My Portfolio</span>
                </h2>
              </div>
            </div>
          </div>
          <div className="pw-loading">
            <div className="pw-spinner"></div>
            <p>Loading portfolio...</p>
          </div>
        </div>
    );
  }

  if (portfolioNames.length === 0) {
    return (
        <div className="page-content portfolio-page pw-page">
          <div className="pw-header">
            <div className="pw-header-left">
              <div className="pw-header-text">
                <h2 className="pw-title">
                  <Briefcase size={24} className="pw-title-icon pw-title-icon--portfolio" />
                  <span>My Portfolio</span>
                </h2>
              </div>
            </div>
          </div>
          <div className="pw-empty-list">
            <Briefcase size={44} />
            <h3>No Portfolios Yet</h3>
            <p>Create your first portfolio to start tracking investments.</p>
            <button onClick={() => setShowCreateModal(true)} className="pw-btn pw-btn-primary">
              <Plus size={16} /> Create Portfolio
            </button>
          </div>
          
          {showCreateModal && (
            <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
              <div className="pw-modal" onClick={(e) => e.stopPropagation()}>
                <div className="pw-modal-content">
                  <h3>Create New Portfolio</h3>
                  <p className="pw-modal-description">Give your portfolio a name, or leave empty for a default.</p>
                  <input
                    type="text"
                    value={newPortfolioName}
                    onChange={(e) => setNewPortfolioName(e.target.value)}
                    placeholder="e.g. Growth Stocks, Dividend Portfolio..."
                    className="pw-modal-input"
                  />
                  <div className="pw-modal-actions">
                    <button onClick={() => setShowCreateModal(false)} className="pw-btn pw-btn-ghost">
                      Cancel
                    </button>
                    <button onClick={handleCreatePortfolio} className="pw-btn pw-btn-primary">
                      Create
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
    );
  }

  return (
      <div className="page-content portfolio-page pw-page">
        <div className="pw-header">
          <div className="pw-header-left">
            <div className="pw-header-text">
              <h2 className="pw-title">
                <Briefcase size={24} className="pw-title-icon pw-title-icon--portfolio" />
                <span>My Portfolio</span>
              </h2>
              <p className="pw-subtitle">Track your investments &amp; performance</p>
            </div>
          </div>
          <div className="pw-header-actions">
            <button
              onClick={handleRefresh}
              className="pw-icon-btn"
              disabled={isRefreshing}
              title="Refresh portfolio data"
              aria-label="Refresh portfolio data"
            >
              <RefreshCw size={24} className={isRefreshing ? 'spinning' : ''} />
            </button>
            {canCreatePortfolio && (
              <button onClick={() => setShowCreateModal(true)} className="pw-btn pw-btn-primary">
                <Plus size={16} /> New Portfolio
              </button>
            )}
            {activePortfolio && portfolioNames.length > 0 && (
              <button
                onClick={() => {
                  setPortfolioToDelete(activePortfolio);
                  setShowDeleteConfirm(true);
                }}
                className="pw-btn pw-btn-ghost-danger"
                title="Delete current portfolio"
                aria-label="Delete current portfolio"
              >
                <Trash2 size={16} />
                <span className="pw-btn-label-collapse">Delete</span>
              </button>
            )}
          </div>
        </div>

        {portfolioNames.length > 0 && (
          <div className="pw-tabs" role="tablist" aria-label="Portfolios">
            {portfolioNames.map(name => (
              <button
                key={name}
                role="tab"
                aria-selected={activePortfolio === name}
                className={`pw-tab ${activePortfolio === name ? 'active' : ''}`}
                onClick={() => handlePortfolioTabChange(name)}
              >
                <span className="pw-tab-name">
                  {name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </span>
                <span className="pw-tab-count">{portfolioCounts[name] ?? 0}</span>
              </button>
            ))}
          </div>
        )}

        <div className="pw-hero-card">
          <div className="pw-hero-main">
            <div className="pw-hero-label">
              <PieChart size={14} />
              <span>Total Portfolio Value</span>
            </div>
            <div className="pw-hero-value">{formatCurrency(portfolioSummary.totalMarketValue)}</div>
            <div className={`pw-hero-pl ${totalPL >= 0 ? 'positive' : 'negative'}`}>
              {totalPL >= 0 ? <ArrowUpRight size={18} /> : <ArrowDownRight size={18} />}
              <span className="pw-hero-pl-amount">{totalPL >= 0 ? '+' : '-'}{formatCurrency(Math.abs(totalPL))}</span>
              <span className="pw-hero-pl-pct">({totalPL >= 0 ? '+' : ''}{formatPercentage(totalPLPct)})</span>
              <span className="pw-hero-pl-suffix">All-time</span>
            </div>
          </div>
          <div className="pw-hero-stats">
            <div className="pw-hero-stat">
              <span className="pw-hero-stat-label"><DollarSign size={12} /> Total Cost</span>
              <span className="pw-hero-stat-value">{formatCurrency(portfolioSummary.totalCost)}</span>
            </div>
            <div className="pw-hero-stat">
              <span className="pw-hero-stat-label">
                {portfolioSummary.avg1WeekPerformance >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                {' '}1W Avg
              </span>
              <span className={`pw-hero-stat-value ${portfolioSummary.avg1WeekPerformance >= 0 ? 'positive' : 'negative'}`}>
                {formatPercentage(portfolioSummary.avg1WeekPerformance)}
              </span>
            </div>
            <div className="pw-hero-stat">
              <span className="pw-hero-stat-label">
                {portfolioSummary.avg4WeekPerformance >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                {' '}1M Avg
              </span>
              <span className={`pw-hero-stat-value ${portfolioSummary.avg4WeekPerformance >= 0 ? 'positive' : 'negative'}`}>
                {formatPercentage(portfolioSummary.avg4WeekPerformance)}
              </span>
            </div>
            <div className="pw-hero-stat">
              <span className="pw-hero-stat-label">
                {avgReturns >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                {' '}AVG Returns
              </span>
              <span className={`pw-hero-stat-value ${avgReturns >= 0 ? 'positive' : 'negative'}`}>
                {formatPercentage(avgReturns)}
              </span>
            </div>
          </div>
        </div>

        <div className={`pw-management-card ${showManagePanel ? 'open' : 'collapsed'}`}>
          <button
            type="button"
            className="pw-management-toggle"
            onClick={() => {
              setShowManagePanel((p) => !p);
              setAutoOpenedByCheckbox(false);
            }}
            aria-expanded={showManagePanel}
          >
            <span className="pw-management-toggle-label">
              <Plus size={16} /> Manage Positions
              {selectedKeys.size > 0 && (
                <span className="pw-management-toggle-badge">{selectedKeys.size} selected</span>
              )}
            </span>
            <ChevronDown size={16} className={`pw-management-toggle-icon ${showManagePanel ? 'open' : ''}`} />
          </button>
          {showManagePanel && (<>
          <div className="pw-management-header">
            <span className="pw-management-hint">
              New symbol → added. Existing symbol → quantity adjusted (use negative to reduce).
            </span>
          </div>
          <form onSubmit={submitPositions} className="pw-form pw-positions-form">
            <div className="pw-position-rows">
              {rows.map((row, idx) => {
                const compositeKey = row.resolvedId ? `${row.resolvedId}|${row.actionType}` : null;
                const existingEntry = compositeKey ? portfolio[compositeKey] : null;
                const existingQty = existingEntry ? parseInt(existingEntry[3]) : null;
                const deltaQty = parseInt(row.qty) || 0;
                const projectedQty = existingQty !== null ? existingQty + deltaQty : null;
                const isExisting = existingEntry != null;
                return (
                  <div key={row.id} className="pw-position-row-wrap">
                    <div className="pw-position-row-label">
                      {isExisting ? (
                        <span className="pw-pos-hint pw-pos-existing">
                          Existing · {existingQty} shares @ {parseFloat(existingEntry[0]).toFixed(2)}
                          {projectedQty !== null && (
                            <span className={`pw-pos-projected ${projectedQty < 1 ? 'pw-pos-danger' : projectedQty < existingQty ? 'pw-pos-warn' : 'pw-pos-ok'}`}>
                              &nbsp;→ {projectedQty} shares
                            </span>
                          )}
                        </span>
                      ) : row.resolvedId ? (
                        <span className="pw-pos-hint pw-pos-new">New position</span>
                      ) : (
                        <span className="pw-pos-hint pw-pos-empty">Row {idx + 1}</span>
                      )}
                    </div>
                    <div className="pw-form-row pw-position-row">
                      <div className="portfolio-symbol-input-wrapper">
                        <SymbolAutocomplete
                          value={row.symbol}
                          onChange={(v) => updateRow(row.id, 'symbol', v)}
                          placeholder="Symbol..."
                          className="symbol-input"
                          disabled={isUpdating}
                          showValidation={true}
                          maxSuggestions={10}
                        />
                      </div>
                      <input
                        type="number"
                        value={row.price}
                        onChange={(e) => updateRow(row.id, 'price', e.target.value)}
                        placeholder="Price"
                        step="0.01"
                        min="0"
                        className="symbol-input pw-buy-price"
                        title="Validated against day's trading range"
                      />
                      <input
                        type="number"
                        value={row.qty}
                        onChange={(e) => updateRow(row.id, 'qty', e.target.value)}
                        placeholder={isExisting ? '±Qty' : 'Qty'}
                        step="1"
                        className="symbol-input pw-qty-input"
                        title={isExisting ? 'Positive to add shares, negative to reduce' : 'Number of shares to add (min 1)'}
                      />
                      <select
                        value={row.actionType}
                        onChange={(e) => updateRow(row.id, 'actionType', e.target.value)}
                        className="symbol-input pw-action-select"
                      >
                        <option value="long">Long</option>
                        <option value="short">Short</option>
                      </select>
                      <div className="date-picker-wrapper">
                        <DatePicker
                          selected={row.date}
                          onChange={(date) => updateRow(row.id, 'date', date)}
                          includeDates={allTradeDates}
                          dateFormat="yyyy-MM-dd"
                          filterDate={d => allTradeDates.some(td => td.getTime() === d.getTime())}
                          placeholderText="Trade Date"
                          showMonthDropdown
                          showYearDropdown
                          className="date-picker"
                        />
                        <ChevronDown className="date-picker-icon" />
                      </div>
                      {rows.length > 1 && (
                        <button
                          type="button"
                          className="pw-btn pw-row-remove-btn"
                          onClick={() => removeRow(row.id)}
                          title="Remove this row"
                          disabled={isUpdating}
                        >
                          <Minus size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="pw-positions-footer">
              <div className="pw-positions-footer-left">
                <button type="button" className="pw-btn pw-btn-add-row" onClick={addRow} disabled={isUpdating}>
                  <Plus size={24} /> 
                </button>
                <button type="submit" className="pw-btn pw-btn-primary pw-btn-sm" disabled={isUpdating}>
                  {isUpdating ? <RefreshCw size={14} className="spinning" /> : <RefreshCw size={14} />}
                  {isUpdating ? 'Saving…' : 'Save'}
                </button>
                {selectedKeys.size > 0 && (
                  <button
                    type="button"
                    className="pw-btn pw-btn-outline-danger pw-btn-sm"
                    onClick={removeSelected}
                    disabled={isUpdating}
                  >
                    <Minus size={14} /> Remove {selectedKeys.size} selected
                  </button>
                )}
              </div>
              <div className="pw-positions-footer-right"></div>
            </div>
          </form>
          </>)}
        </div>

        {isLoadingPortfolioData ? (
          <div className="pw-loading">
            <div className="pw-spinner"></div>
            <p>Loading portfolio data...</p>
          </div>
        ) : isPortfolioEmpty ? (
          <div className="pw-empty-state">
            <Briefcase size={40} />
            <h3>No Holdings Yet</h3>
            <p>Add symbols above to start tracking your investments.</p>
          </div>
        ) : (
          <div className="pw-data-card">
            <div className="pw-data-header">
              <div className="pw-data-header-left">
                <h3>Holdings</h3>
                <span className="pw-data-count">{portfolioStocks?.length || 0} positions</span>
              </div>
              <div className="pw-data-header-right">
                <div className="pw-search-wrap">
                  <Search size={14} className="pw-search-icon" />
                  <input
                    type="text"
                    className="pw-search-input"
                    placeholder="Search symbol, company, sector..."
                    value={tableSearch}
                    onChange={(e) => setTableSearch(e.target.value)}
                  />
                  {tableSearch && (
                    <button type="button" className="pw-search-clear" onClick={() => setTableSearch('')} aria-label="Clear search">×</button>
                  )}
                </div>
              </div>
            </div>
            {paginatedStocks.length > 0 ? (
              <>
                <div className="pw-table-wrap">
                  <table className="pw-table pw-table--portfolio pw-table--sticky-symbol">
                    <thead>
                      <tr>
                        <th className="pw-cb-col">
                          <input
                            type="checkbox"
                            className="pw-checkbox"
                            title="Select all"
                            checked={paginatedStocks.length > 0 && paginatedStocks.every(s => selectedKeys.has(s.composite_key))}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedKeys(prev => new Set([...prev, ...paginatedStocks.map(s => s.composite_key)]));
                              } else {
                                setSelectedKeys(prev => {
                                  const next = new Set(prev);
                                  paginatedStocks.forEach(s => next.delete(s.composite_key));
                                  return next;
                                });
                              }
                            }}
                          />
                        </th>
                        {renderSortHeader(colByKey('symbol'), 'pw-col-symbol')}
                        {renderSortHeader(colByKey('name'), 'pw-col-company')}
                        {renderSortHeader(colByKey('sector'))}
                        <th>Instrument</th>
                        <th>Ccy</th>
                        {renderSortHeader(colByKey('action_type'))}
                        {renderSortHeader(colByKey('close'))}
                        {renderSortHeader(colByKey('days_change'))}
                        {renderSortHeader(colByKey('trade_timestamp'))}
                        {renderSortHeader(colByKey('price_ext'))}
                        {renderSortHeader(colByKey('pct_change_ext'))}
                        {renderSortHeader(colByKey('buy_price'))}
                        {renderSortHeader(colByKey('quantity'))}
                        {renderSortHeader(colByKey('cost'))}
                        {renderSortHeader(colByKey('market_value'))}
                        {renderSortHeader(colByKey('performance'))}
                        {renderSortHeader(colByKey('buy_date'))}
                        <th>Signal</th>
                        <th>News</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedStocks.map((stock) => (
                        <tr
                          key={stock.composite_key}
                          className={selectedKeys.has(stock.composite_key) ? 'pw-row-selected' : ''}
                        >
                          <td className="pw-cb-col">
                            <input
                              type="checkbox"
                              className="pw-checkbox"
                              checked={selectedKeys.has(stock.composite_key)}
                              onChange={(e) => {
                                setSelectedKeys(prev => {
                                  const next = new Set(prev);
                                  if (e.target.checked) next.add(stock.composite_key);
                                  else next.delete(stock.composite_key);
                                  return next;
                                });
                              }}
                            />
                          </td>
                          <td className="pw-col-symbol">
                            <a href={`/stock/${encodeURIComponent(String(stock.symbol_id).toUpperCase())}`} target="_blank" rel="noopener noreferrer" className="pw-symbol-link">
                              {stock.symbol}
                            </a>
                          </td>
                          <td className="pw-company-name pw-col-company">{stock.name}</td>
                          <td className="pw-sector">{stock.sector}</td>
                          <td className="pw-instrument">{stock.instrument}</td>
                          <td className="pw-currency">{stock.currency}</td>
                          <td>
                            <span className={`pw-action-badge pw-action-${stock.action_type}`}>
                              {stock.action_type}
                            </span>
                          </td>
                          <td>{stock.close ? stock.close.toFixed(2) : 'N/A'}</td>
                          <td>
                            <span className={`pw-change ${stock.days_change >= 0 ? 'positive' : 'negative'}`}>
                              {stock.days_change >= 0 ? '+' : ''}{stock.days_change != null ? stock.days_change.toFixed(2) : 'N/A'}%
                            </span>
                          </td>
                          <td>{stock.trade_timestamp || '--'}</td>
                          <td>{stock.price_ext != null ? `${Number(stock.price_ext).toFixed(2)}` : '--'}</td>
                          <td>
                            <span className={`pw-change ${stock.pct_change_ext > 0 ? 'positive' : stock.pct_change_ext < 0 ? 'negative' : ''}`}>
                              {stock.pct_change_ext != null ? (stock.pct_change_ext > 0 ? '+' : '') + Number(stock.pct_change_ext).toFixed(2) + '%' : '--'}
                            </span>
                          </td>
                          <td>{stock.buy_price ? stock.buy_price.toFixed(2) : 'N/A'}</td>
                          <td className="pw-qty">{stock.quantity}</td>
                          <td>{stock.cost ? stock.cost.toFixed(2) : 'N/A'}</td>
                          <td>{stock.market_value ? stock.market_value.toFixed(2) : 'N/A'}</td>
                          <td>
                            <span className={`pw-change ${stock.performance >= 0 ? 'positive' : 'negative'}`}>
                              {stock.performance >= 0 ? '+' : ''}{stock.performance != null ? stock.performance.toFixed(2) : 'N/A'}%
                            </span>
                          </td>
                          <td>{stock.buy_date || 'N/A'}</td>
                          <td>
                            <span className={`pw-status-badge ${stock.status === 'buy' ? 'pw-status-buy' : stock.status === 'sell' ? 'pw-status-sell' : 'pw-status-hold'}`}>
                              {stock.status}
                            </span>
                          </td>
                          <td>
                            <div className="news-flag-container">
                              <span 
                                className={`news-flag news-flag-${newsFlags[stock.symbol_id] || 'grey'}`}
                                title={getNewsFlagTooltip(newsFlags[stock.symbol_id] || 'grey')}
                              >
                                ●
                              </span>
                              {newsFlagRatings[stock.symbol_id] != null && (
                                <span className="news-rating-box" title="News sentiment rating">
                                  {renderNewsRating(newsFlagRatings[stock.symbol_id])}
                                </span>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="pw-mobile-list">
                  {paginatedStocks.map((stock) => (
                    <div
                      key={stock.composite_key}
                      className={`pw-mobile-row ${selectedKeys.has(stock.composite_key) ? 'is-selected' : ''}`}
                    >
                      <div className="pw-mobile-row-top">
                        <label className="pw-mobile-check">
                          <input
                            type="checkbox"
                            className="pw-checkbox"
                            checked={selectedKeys.has(stock.composite_key)}
                            onChange={(e) => {
                              setSelectedKeys((prev) => {
                                const next = new Set(prev);
                                if (e.target.checked) next.add(stock.composite_key);
                                else next.delete(stock.composite_key);
                                return next;
                              });
                            }}
                          />
                        </label>
                        <div className="pw-mobile-symbol">
                          <a href={`/stock/${encodeURIComponent(String(stock.symbol_id).toUpperCase())}`} target="_blank" rel="noopener noreferrer" className="pw-symbol-link">{stock.symbol}</a>
                          <span className="pw-mobile-name">{stock.name}</span>
                        </div>
                        <div className={`pw-mobile-price ${stock.days_change >= 0 ? 'positive' : 'negative'}`}>
                          <span className="pw-mobile-price-val">{stock.close ? stock.close.toFixed(2) : 'N/A'}</span>
                          <span className="pw-mobile-price-pct">{stock.days_change >= 0 ? '+' : ''}{stock.days_change != null ? stock.days_change.toFixed(2) : 'N/A'}%</span>
                        </div>
                      </div>
                      <div className="pw-mobile-row-grid">
                        <div className="pw-mobile-cell"><span>Action</span><span className={`pw-action-badge pw-action-${stock.action_type}`}>{stock.action_type}</span></div>
                        <div className="pw-mobile-cell"><span>Qty</span><strong>{stock.quantity}</strong></div>
                        <div className="pw-mobile-cell"><span>Buy Price</span><strong>{stock.buy_price ? stock.buy_price.toFixed(2) : '–'}</strong></div>
                        <div className="pw-mobile-cell"><span>Cost</span><strong>{stock.cost ? stock.cost.toFixed(2) : '–'}</strong></div>
                        <div className="pw-mobile-cell"><span>Mkt Value</span><strong>{stock.market_value ? stock.market_value.toFixed(2) : '–'}</strong></div>
                        <div className="pw-mobile-cell">
                          <span>Returns</span>
                          <strong className={`pw-change ${stock.performance >= 0 ? 'positive' : 'negative'}`}>
                            {stock.performance >= 0 ? '+' : ''}{stock.performance != null ? stock.performance.toFixed(2) : 'N/A'}%
                          </strong>
                        </div>
                        <div className="pw-mobile-cell"><span>Buy Date</span><strong>{stock.buy_date || '–'}</strong></div>
                        <div className="pw-mobile-cell"><span>Updated At</span><strong>{stock.trade_timestamp || '–'}</strong></div>
                        <div className="pw-mobile-cell">
                          <span>Signal</span>
                          <span className={`pw-status-badge ${stock.status === 'buy' ? 'pw-status-buy' : stock.status === 'sell' ? 'pw-status-sell' : 'pw-status-hold'}`}>{stock.status}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="pw-pagination">
                  <button onClick={() => setPage(1)} disabled={page === 1} title="First Page" aria-label="First Page">
                    <ChevronsLeft size={18} />
                  </button>
                  <button onClick={() => setPage(prev => Math.max(prev - 1, 1))} disabled={page === 1} title="Previous Page" aria-label="Previous Page">
                    <ChevronLeft size={18} />
                  </button>
                  <span className="pw-page-info">Page {page} of {totalPages}</span>
                  <button onClick={() => setPage(prev => Math.min(prev + 1, totalPages))} disabled={page === totalPages} title="Next Page" aria-label="Next Page">
                    <ChevronRight size={18} />
                  </button>
                  <button onClick={() => setPage(totalPages)} disabled={page === totalPages} title="Last Page" aria-label="Last Page">
                    <ChevronsRight size={18} />
                  </button>
                </div>
              </>
            ) : (
              <div className="pw-empty-state pw-empty-state--inline">
                <Search size={32} />
                <h3>No matches</h3>
                <p>{tableSearch ? `No holdings match "${tableSearch}"` : 'No holdings to display.'}</p>
                {tableSearch && (
                  <button onClick={() => setTableSearch('')} className="pw-btn pw-btn-ghost pw-btn-sm">Clear search</button>
                )}
              </div>
            )}
          </div>
        )}
        
        {showCreateModal && (
          <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
            <div className="pw-modal" onClick={(e) => e.stopPropagation()}>
              <div className="pw-modal-content">
                <h3>Create New Portfolio</h3>
                <p className="pw-modal-description">Give your portfolio a name, or leave empty for a default.</p>
                <input
                  type="text"
                  value={newPortfolioName}
                  onChange={(e) => setNewPortfolioName(e.target.value)}
                  placeholder="e.g. Growth Stocks, Dividend Portfolio..."
                  className="pw-modal-input"
                />
                <div className="pw-modal-actions">
                  <button onClick={() => setShowCreateModal(false)} className="pw-btn pw-btn-ghost">
                    Cancel
                  </button>
                  <button onClick={handleCreatePortfolio} className="pw-btn pw-btn-primary">
                    Create
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {showDeleteConfirm && (
          <div className="modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
            <div className="pw-modal" onClick={(e) => e.stopPropagation()}>
              <div className="pw-modal-content">
                <h3>Delete Portfolio</h3>
                <p className="pw-modal-description">
                  Are you sure you want to delete <strong>"{portfolioToDelete}"</strong>? This action cannot be undone.
                </p>
                <div className="pw-modal-actions">
                  <button onClick={() => setShowDeleteConfirm(false)} className="pw-btn pw-btn-ghost">
                    Cancel
                  </button>
                  <button onClick={handleDeletePortfolio} className="pw-btn pw-btn-danger">
                    Delete Portfolio
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {pendingConfirm && (
          <div className="modal-overlay" onClick={() => setPendingConfirm(null)}>
            <div className="pw-modal pw-modal-warn" onClick={(e) => e.stopPropagation()}>
              <div className="pw-modal-content">
                <h3 className="pw-modal-warn-title">Position Quantity Would Drop Below 1</h3>
                <p className="pw-modal-description">
                  The following positions would reach 0 or negative quantity after applying this change.
                  Choose to remove them from the portfolio and continue, or go back to adjust.
                </p>
                <ul className="pw-modal-qty-list">
                  {pendingConfirm.toRemove.map(item => (
                    <li key={item.resolvedId} className="pw-modal-qty-item">
                      <span className="pw-modal-qty-symbol">{item.symbol || item.resolvedId}</span>
                      <span className="pw-modal-qty-math">
                        {item.existingQty} + ({parseInt(item.qty)}) = <strong className="pw-pos-danger">{item.finalQty}</strong>
                      </span>
                    </li>
                  ))}
                </ul>
                <div className="pw-modal-actions">
                  <button onClick={() => setPendingConfirm(null)} className="pw-btn pw-btn-secondary">
                    Go Back
                  </button>
                  <button onClick={handleConfirmRemove} className="pw-btn pw-btn-danger" disabled={isUpdating}>
                    {isUpdating ? 'Updating…' : 'Remove & Continue'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
  );
};

export default Portfolio;